import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface ContactNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  onSuccess?: () => void;
}

interface ContactNote {
  id: string;
  note: string;
  note_type: string;
  created_at: string;
  created_by: string | null;
  profiles?: {
    display_name: string | null;
  };
}

export const ContactNotesDialog = ({
  open,
  onOpenChange,
  contactId,
  onSuccess,
}: ContactNotesDialogProps) => {
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [notes, setNotes] = useState<ContactNote[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      loadNotes();
    }
  }, [open, contactId]);

  const loadNotes = async () => {
    const { data, error } = await supabase
      .from("contact_notes")
      .select(`
        *,
        profiles:created_by(display_name)
      `)
      .eq("contact_id", contactId)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setNotes(data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) {
      toast({
        title: "Error",
        description: "Please enter a note",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase.from("contact_notes").insert({
      contact_id: contactId,
      note: note.trim(),
      note_type: noteType,
      created_by: user?.id,
    });

    setLoading(false);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Note added successfully",
      });
      setNote("");
      loadNotes();
      onSuccess?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Contact Notes</DialogTitle>
          <DialogDescription>
            Add notes and track communication history for this contact
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="note-type">Note Type</Label>
            <Select value={noteType} onValueChange={setNoteType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General Note</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
                <SelectItem value="callback">Callback</SelectItem>
                <SelectItem value="status_change">Status Change</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter your note here..."
              rows={4}
            />
          </div>

          <Button type="submit" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Note
          </Button>
        </form>

        <div className="mt-6 space-y-4">
          <h4 className="font-semibold">Previous Notes</h4>
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No notes yet</p>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium capitalize bg-muted px-2 py-1 rounded">
                    {note.note_type.replace("_", " ")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(note.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm">{note.note}</p>
                {note.profiles?.display_name && (
                  <p className="text-xs text-muted-foreground">
                    By: {note.profiles.display_name}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
